import type { SpinWheelSegment, SpinWheelTheme } from './types';

export interface WheelRenderState {
  rotationDeg: number;
  segments: SpinWheelSegment[];
  theme: SpinWheelTheme;
}

const canvasSize = 480;
const pointerAngleDeg = 270;

export class WheelRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private devicePixelRatio = 1;
  private frame?: number;

  constructor(canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D context is unavailable.');

    this.canvas = canvas;
    this.context = context;
    this.resize();
  }

  isForCanvas(canvas: HTMLCanvasElement): boolean {
    return this.canvas === canvas;
  }

  resize(): void {
    this.devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = canvasSize * this.devicePixelRatio;
    this.canvas.height = canvasSize * this.devicePixelRatio;
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.context.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
  }

  draw(state: WheelRenderState): void {
    const ctx = this.context;
    const center = canvasSize / 2;
    const radius = center - 24;
    const segmentAngle = (Math.PI * 2) / state.segments.length;
    const rotation = (state.rotationDeg * Math.PI) / 180;

    ctx.clearRect(0, 0, canvasSize, canvasSize);
    ctx.save();
    ctx.translate(center, center);
    ctx.beginPath();
    ctx.arc(0, 0, radius + 16, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(15, 23, 42, 0.12)';
    ctx.shadowColor = 'rgba(15, 23, 42, 0.26)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 14;
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.translate(center, center);
    ctx.rotate(rotation);

    ctx.beginPath();
    ctx.arc(0, 0, radius + 10, 0, Math.PI * 2);
    ctx.fillStyle = state.theme.surfaceColor;
    ctx.fill();

    state.segments.forEach((segment, index) => {
      const start = index * segmentAngle - Math.PI / 2;
      const end = start + segmentAngle;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, start, end);
      ctx.closePath();
      ctx.fillStyle = segment.color;
      ctx.fill();
      ctx.strokeStyle = state.theme.surfaceColor;
      ctx.lineWidth = 5;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, start, end);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.fill();

      ctx.save();
      ctx.rotate(start + segmentAngle / 2);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = segment.textColor || state.theme.textColor;
      ctx.font =
        '900 22px Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetY = 1;
      this.drawSegmentText(segment.label, radius - 26, 0, radius * 0.5);
      ctx.restore();
    });

    ctx.beginPath();
    ctx.arc(0, 0, radius + 7, 0, Math.PI * 2);
    ctx.lineWidth = 10;
    ctx.strokeStyle = state.theme.primaryColor;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(0, 0, 50, 0, Math.PI * 2);
    ctx.fillStyle = state.theme.surfaceColor;
    ctx.fill();
    ctx.lineWidth = 7;
    ctx.strokeStyle = state.theme.primaryColor;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(0, 0, 23, 0, Math.PI * 2);
    ctx.fillStyle = state.theme.accentColor;
    ctx.fill();

    ctx.restore();
    this.drawPointer(state.theme);
  }

  animateTo(
    fromRotationDeg: number,
    targetRotationDeg: number,
    durationMs: number,
    render: (rotationDeg: number) => void,
  ): Promise<void> {
    if (this.frame) window.cancelAnimationFrame(this.frame);

    const start = performance.now();
    const change = targetRotationDeg - fromRotationDeg;

    return new Promise((resolve) => {
      const tick = (now: number) => {
        const elapsed = Math.min(1, (now - start) / durationMs);
        const eased = 1 - Math.pow(1 - elapsed, 4);
        render(fromRotationDeg + change * eased);

        if (elapsed < 1) {
          this.frame = window.requestAnimationFrame(tick);
        } else {
          this.frame = undefined;
          resolve();
        }
      };

      this.frame = window.requestAnimationFrame(tick);
    });
  }

  stop(): void {
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = undefined;
    }
  }

  getTargetRotation(
    resultSegmentId: string,
    segments: SpinWheelSegment[],
    currentRotationDeg: number,
    stopAngleDeg?: number,
  ): number {
    const fullTurns = 5 * 360;
    const normalizedCurrent = normalizeDeg(currentRotationDeg);
    const base = currentRotationDeg - normalizedCurrent;

    const index = segments.findIndex((segment) => segment.id === resultSegmentId);
    if (index < 0 && typeof stopAngleDeg === 'number' && Number.isFinite(stopAngleDeg)) {
      return base + fullTurns + normalizeDeg(stopAngleDeg);
    }
    if (index < 0) return currentRotationDeg + fullTurns;

    const segmentSize = 360 / segments.length;
    const segmentCenter = index * segmentSize + segmentSize / 2 - 90;
    const jitter = (Math.random() - 0.5) * segmentSize * 0.28;
    const targetNormalized = normalizeDeg(pointerAngleDeg - segmentCenter + jitter);
    return base + fullTurns + targetNormalized;
  }

  private drawPointer(theme: SpinWheelTheme): void {
    const ctx = this.context;
    const center = canvasSize / 2;

    ctx.save();
    ctx.translate(center, 12);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(24, 42);
    ctx.lineTo(-24, 42);
    ctx.closePath();
    ctx.fillStyle = theme.pointerColor;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = theme.surfaceColor;
    ctx.stroke();
    ctx.restore();
  }

  private drawSegmentText(text: string, x: number, y: number, maxWidth: number): void {
    const ctx = this.context;
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let current = '';

    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (ctx.measureText(next).width <= maxWidth || !current) {
        current = next;
      } else {
        lines.push(current);
        current = word;
      }
    });

    if (current) lines.push(current);

    const limited = lines.slice(0, 2);
    const lineHeight = 24;
    const offset = (limited.length - 1) * lineHeight * -0.5;

    limited.forEach((line, index) => {
      ctx.fillText(line, x, y + offset + index * lineHeight, maxWidth);
    });
  }
}

function normalizeDeg(value: number): number {
  return ((value % 360) + 360) % 360;
}
